import React from "react";

const styles = `
.privacy-policy {
  max-width: 800px;
  margin: 4.5rem auto 0 auto;
  padding: 2rem 1.5rem;
  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;
  color: #1a1a1a;
  line-height: 1.6;
}

.privacy-policy > h1 {
  font-size: 2rem;
  font-weight: 700;
  margin-bottom: 1rem;
}

.privacy-policy > h2 {
  font-size: 1.5rem;
  font-weight: 600;
  margin-bottom: 1rem;
  margin-top: 2rem;
}

.privacy-policy > h3 {
  font-size: 1.25rem;
  font-weight: 600;
  margin-bottom: 1rem;
  margin-top: 2rem;
}

.privacy-policy > p {
  margin-bottom: 1rem;
}

.privacy-policy > ul {
  list-style: disc;
  margin-bottom: 1rem;
}

.privacy-policy > ul > li {
  margin-left: 1rem;
}

.privacy-policy a {
  color: #2563eb;
  text-decoration: underline;
}
`;

export default function PrivacyPolicy() {
  return (
    <>
      <style>{styles}</style>
      <div className="privacy-policy">
        <h1>Privacy Policy</h1>

        <p>
          This Privacy Policy describes how SalvageMe ("we", "us", "our")
          collects, uses, shares, and safeguards your information.
        </p>

        <h2>Overview</h2>

        <p>
          This Privacy Policy applies to our website, applications, products,
          and services that link to this policy or otherwise refer you to
          this policy (collectively, our "Services").
        </p>


        <h2>Information Collection and Use</h2>

        <h3>Personal Data</h3>

        <p>
          When you use our Services, we may collect Personal Data, which
          refers to any information that identifies you personally. This
          includes, but is not limited to:
        </p>

        <ul>
          <li>
            Contact information such as your name, email address, mailing
            address, and phone number.
          </li>
          <li>
            Demographic information such as age, education, gender,
            interests, and zip code.
          </li>
        </ul>

        <h3>Non-Personal Data</h3>

        <p>
          We may also collect non-personal data, which is data that cannot be
          directly linked to an individual. This includes browser and device
          information, application usage data, information collected through
          cookies, pixel tags, and other technologies, and demographic
          information.
        </p>

        <h3>Use of Information</h3>

        <p>We use the information we collect for purposes including:</p>

        <ul>
          <li>Providing, maintaining, and improving our Services.</li>
          <li>Processing transactions and sending related information.</li>
          <li>Sending promotional communications.</li>
          <li>Responding to user inquiries and providing customer support.</li>
          <li>Conducting research and analysis.</li>
          <li>Ensuring network and information security.</li>
        </ul>

        <h2>Information Sharing</h2>

        <p>
          We may share your personal information with third parties under the
          following circumstances:
        </p>

        <ul>
          <li>With your consent.</li>
          <li>
            With third-party vendors and other service providers that perform
            services on our behalf.
          </li>
          <li>
            If we believe disclosure is necessary to comply with relevant
            laws, to respond to legal requests, or to protect our rights,
            property, or safety.
          </li>
          <li>
            In connection with any merger, sale of company assets, or
            acquisition.
          </li>
        </ul>

        <h2>Cookies and Tracking Technologies</h2>

        <p>
          We may use logs and various other tracking technologies to
          automatically collect information. You have the right to disable
          cookies at your browser level, though this may affect your ability
          to use certain features of our Services.
        </p>

        <h2>Security</h2>

        <p>
          We employ reasonable physical, technical, and administrative
          measures designed to safeguard the information we collect. However,
          no security measure is 100% secure, and we cannot guarantee the
          absolute security of your information.
        </p>

        <h2>International Data Transfers</h2>

        <p>
          Personal information that you submit through the Services may be
          transferred to countries other than where you live, such as, for
          example, to our servers in the U.S. Your personal information may
          be subject to privacy laws that are different from those in your
          country.
        </p>

        <h2>Your Rights and Choices</h2>

        <p>
          Depending on your location, you may have certain rights in relation
          to your personal information. These may include: the right to
          access, rectification, erasure, restrict processing, or object to
          processing of your personal data, and the right to data
          portability.
        </p>

        <h2>Children's Privacy</h2>

        <p>
          Our Services are not directed to individuals under 2. We do not
          knowingly collect personal information from children under 2. If
          we become aware that a child under 2 has provided us with personal
          information, we will take steps to delete such information.
        </p>

        <h2>GDPR Notice</h2>

        <h3>Legal Basis for Processing Personal Data</h3>

        <p>
          Under the General Data Protection Regulation (GDPR), we must have a
          legal basis to process your personal information. We process your
          information on the following legal bases:
        </p>

        <ul>
          <li>
            You have consented to the use of your personal information. For
            example, we may seek to obtain your consent for our uses of
            cookies.
          </li>
          <li>
            We need your personal information to provide you with services
            and products requested by you, or to respond to your inquiries.
          </li>
          <li>We have a legal obligation to use your personal information.</li>
          <li>We have a legitimate interest in using your personal information.</li>
        </ul>

        <h3>Transfers of Personal Data</h3>

        <p>
          The personal data that we collect from you may be stored and
          processed in your region, or transferred to, stored at or otherwise
          processed outside the European Economic Area ("EEA"), including but
          not limited to the United States, where we have our data centers.
        </p>

        <h3>Retention of Personal Data</h3>

        <p>
          We will retain your personal information for as long as necessary
          to provide our services, and as necessary to comply with our legal
          obligations, resolve disputes, and enforce our policies.
        </p>

        <h3>Data Subject Rights</h3>

        <p>Under the GDPR, you have the following rights:</p>

        <ul>
          <li>
            Right to Access: You have the right to request access to your
            personal data that we hold.
          </li>
          <li>
            Right to Rectification: You have the right to correct any
            inaccurate personal data about you and to complete any incomplete
            personal data about you.
          </li>
          <li>
            Right to Erasure (the "Right to be Forgotten"): In certain
            circumstances, you have the right to the erasure of your personal
            data from our systems.
          </li>
          <li>
            Right to Restrict Processing: In certain circumstances, you have
            the right to restrict the processing of your personal data.
          </li>
          <li>
            Right to Data Portability: You have the right to receive your
            personal data in a structured, commonly used and machine-readable
            format and have the right to transmit those data to another
            controller.
          </li>
          <li>
            Right to Object: In certain circumstances, you have the right to
            object to the processing of your personal data.
          </li>
          <li>
            Right to Withdraw Consent: If you have given your consent to
            anything we do with your personal data, you have the right to
            withdraw your consent at any time.
          </li>
        </ul>

        <h3>Exercising Your GDPR Data Protection Rights</h3>

        <p>
          You may exercise any of your rights in relation to your personal
          data by written notice to us addressed to the following:{" "}
          salvagemeinitiative@gmail.com
        </p>

        <h3>Data Protection Officer</h3>

        <p>
          We have appointed a Data Protection Officer (DPO) who is
          responsible for overseeing questions in relation to this Privacy
          Policy. If you have any questions about this Privacy Policy,
          including any requests to exercise your legal rights, please
          contact the DPO at salvagemeinitiative@gmail.com.
        </p>

        <h2>CCPA Notice</h2>

        <h3>Notice of Collection and Use of Personal Information</h3>

        <p>We may collect the following categories of personal information about you:</p>

        <ul>
          <li>Identifiers: such as name, address, email address.</li>
          <li>
            Personal information: as defined in the California customer
            records law, such as contact information and financial
            information.
          </li>
          <li>Internet activity: such as your interactions with our Service.</li>
          <li>Geolocation data: such as device location.</li>
          <li>
            Professional or employment-related information: such as job
            history or employer information.
          </li>
          <li>
            We may use your personal information for the purposes listed in
            this Privacy Policy.
          </li>
        </ul>

        <h3>Our Personal Information Selling Practices</h3>

        <p>
          We do not sell your personal information in the traditional sense.
          We do share your information as outlined in the Information Sharing
          section of this Privacy Policy.
        </p>

        <h3>Rights Under CCPA</h3>

        <p>If you are a California resident, you have the following rights:</p>

        <ul>
          <li>
            Right to Know: You have the right to request that we disclose
            what personal information we collect, use, disclose, and sell
            about you.
          </li>
          <li>
            Right to Delete: You have the right to request the deletion of
            your personal information that we have collected or maintain,
            subject to certain exceptions.
          </li>
          <li>
            Right to Opt-Out: You have the right to opt out of the sale of
            your personal information.
          </li>
          <li>
            Right to Non-Discrimination: You have the right not to receive
            discriminatory treatment for exercising your CCPA rights.
          </li>
        </ul>

        <h3>Exercising Your CCPA Data Protection Rights</h3>

        <p>To exercise any of your rights under the CCPA, please contact us using the following methods:</p>

        <ul>
          <li>Email: salvagemeinitiative@gmail.com</li>
          <li>
            Website:{" "}
            <a
              href="https://salvageme.papakowdadson.com/"
              target="_blank"
              rel="noopener noreferrer"
            >
              https://salvageme.papakowdadson.com/
            </a>
          </li>
        </ul>

        <p>
          Please note that we may need to verify your identity before
          processing your request, which may require additional personal
          information from you. In certain circumstances, we may decline or
          limit your request, particularly where we are unable to verify your
          identity or locate your information in our systems, or as permitted
          by law.
        </p>

        <h2>Changes to this Privacy Policy</h2>

        <p>
          We reserve the right to modify this Privacy Policy at any time, so
          please review it frequently. Changes will be posted on this page
          and your continued use of our Services after any changes have been
          posted will constitute your agreement to such changes.
        </p>

        <h2>Contact Us</h2>

        <p>
          If you have any questions about this Privacy Policy, please contact
          us at salvagemeinitiative@gmail.com.
        </p>

        <p>Last updated: 25/08/2026</p>
      </div>
    </>
  );
}